import React from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

/**
 * The platform's button.
 *
 * ## Icons: two slots, two jobs
 *
 * Which side an icon sits on is not decoration, it is meaning — mixing the two
 * is what let three buttons in the same header end up with three different
 * treatments:
 *
 * - **`icon` (left) says what the action IS.** It reads as one phrase with the
 *   label: "⬇ Descargar Etiqueta". Download, Plus, Trash, Send.
 * - **`iconRight` says what happens NEXT.** A chevron for "opens a menu", an
 *   external-link for "opens a tab", an arrow for "advances a step". It is a
 *   promise about the consequence, not a description of the action.
 *
 * A button can carry both — an edit menu is `✏️ Editar ⌄`.
 *
 * ## Where icons do not belong
 *
 * If every button has one, the icon stops being information and becomes
 * texture. Skip it on Save/Cancel inside a dialog: the dialog already said what
 * is happening. Always keep it on destructive actions, where half a second of
 * recognition matters. Use `iconOnly` only inside table rows, and pass an
 * `aria-label` with it.
 *
 * ## Hierarchy
 *
 * `primary` is the brand green and marks the main action; there should be one
 * per surface. `secondary` is the filled grey beside it, `ghost` the bordered
 * one for anything further down, and `danger` is reserved for destruction.
 *
 * ## Rows to tap
 *
 * `choice` is a row the whole of which is the button: a warehouse, a candidate,
 * a line of a queue. It takes the full width, at least 44 px high, and the
 * children lay it out, left-aligned and on as many lines as they need; so
 * `size`, `icon`, `iconRight` and `iconOnly` do not apply to it, and an icon
 * goes in the children. A row that stays chosen, like a tab, says so with
 * `aria-pressed`, which fills it green. With `pill` it is fully round: a chip,
 * as wide as the `li` it sits in.
 *
 * `<Button variant="choice" onClick={pick} data-testid="warehouse-option">…</Button>`
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'ghost'|'outline'|'danger'|'subtle'|'choice'} [props.variant='secondary']
 * @param {'xs'|'sm'|'md'|'lg'} [props.size='md'] - `xs` is for a toolbar that has to
 *   fit several actions in one row, like the deliveries selection bar.
 * @param {React.ComponentType} [props.icon] - Rendered left: what the action is.
 * @param {React.ComponentType} [props.iconRight] - Rendered right: what follows.
 * @param {boolean} [props.loading=false] - Replaces `icon` with a spinner and
 *   disables the button, so a click cannot be sent twice.
 * @param {string} [props.loadingLabel] - Shown instead of the children while
 *   loading, e.g. "Guardando...".
 * @param {boolean} [props.pill=false] - Fully round. Used by dialog footers.
 * @param {boolean} [props.iconOnly=false] - Square, no label. Needs `aria-label`.
 * @param {boolean} [props.fullWidth=false]
 */
export const Button = React.forwardRef(({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  iconRight: IconRight,
  loading = false,
  loadingLabel,
  pill = false,
  iconOnly = false,
  fullWidth = false,
  disabled = false,
  className = '',
  children,
  ...rest
}, ref) => {
  const choice = variant === 'choice';
  const pressed = rest['aria-pressed'] === true || rest['aria-pressed'] === 'true';
  const variants = {
    primary: 'bg-ninja-green hover:bg-ninja-green/90 text-white shadow-sm',
    secondary:
      'bg-ninja-gray dark:bg-[#2d2d2d] text-ninja-dark dark:text-white hover:opacity-90',
    ghost:
      'border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white',
    outline:
      'border-2 border-ninja-green text-ninja-green hover:bg-ninja-green hover:text-white',
    danger: 'bg-danger hover:bg-danger/90 text-white shadow-sm',
    // No chrome of its own — for icons living inside a table row.
    subtle:
      'text-text-muted hover:text-ninja-dark dark:hover:text-white hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]',
    // A row to tap; `aria-pressed` marks the chosen one.
    choice: pressed
      ? 'border-ninja-green bg-ninja-green text-white'
      : 'border-border bg-white text-ninja-dark hover:border-ninja-green active:bg-ninja-gray dark:bg-[#1a1a1a] dark:text-white dark:active:bg-[#222]',
  };

  const sizes = {
    xs: iconOnly ? 'w-7 h-7' : 'px-2.5 py-1.5 text-xs gap-1.5',
    sm: iconOnly ? 'w-8 h-8' : 'px-3 py-1.5 text-sm gap-1.5',
    md: iconOnly ? 'w-10 h-10' : 'px-5 py-2.5 text-sm gap-2',
    lg: iconOnly ? 'w-12 h-12' : 'px-6 py-3 text-base gap-2',
  };

  const iconSize = { xs: 14, sm: 14, md: 16, lg: 18 }[size];
  const LeftIcon = loading ? Loader2 : Icon;
  const radius = choice ? 'rounded-2xl' : 'rounded-xl';

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        choice
          ? 'block w-full min-h-[44px] border p-4 text-left shadow-sm transition-colors'
          : 'inline-flex items-center justify-center font-bold transition-colors',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        variants[variant],
        !choice && sizes[size],
        pill ? 'rounded-full' : radius,
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {LeftIcon && (
        <LeftIcon size={iconSize} className={clsx('shrink-0', loading && 'animate-spin')} />
      )}
      {!iconOnly && (loading && loadingLabel ? loadingLabel : children)}
      {IconRight && !loading && <IconRight size={iconSize} className="shrink-0" />}
    </button>
  );
});

Button.displayName = 'Button';
